import { useState } from 'react';
import toast from 'react-hot-toast';
import { useDelayAlertStore } from '../hooks/useDelayAlertStore';
import { Dialog, DialogPortal, DialogOverlay, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

interface Props {
    appointmentId: string | null;
    onClose: () => void;
}

export function DelayResolutionModal({ appointmentId, onClose }: Props) {
    const [action, setAction] = useState<'decision' | 'check_in' | 'reason' | 'eta'>('decision');
    const [reason, setReason] = useState('');
    const [newEta, setNewEta] = useState('');
    const [loading, setLoading] = useState(false);
    const { removeAlert } = useDelayAlertStore();

    if (!appointmentId) return null;

    const REASONS = [
        'Carrier Delayed - Notified',
        'Carrier Delayed - No Update',
        'Warehouse Full / Congestion',
        'Weather / Road Conditions',
        'Mechanical Failure',
        'Other'
    ];

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const payload: any = {};
            if (action === 'reason') {
                payload.action = 'reason';
                payload.reason = reason;
            } else if (action === 'eta') {
                payload.action = 'new_eta';
                payload.newEta = new Date(newEta).toISOString();
                // Optionally add reason too if allowed, but strict separation for now
                if (reason) payload.reason = reason;
            }

            const res = await fetch(`/api/appointments/${appointmentId}/resolve-delay`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}` // Use 'authToken' from useAuth
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) throw new Error('Resolution failed');

            toast.success('Delay resolved successfully');

            // Critical: Remove alert from global store on success
            removeAlert(appointmentId);

            onClose();
        } catch (error) {
            toast.error('Failed to update appointment');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckInRedirect = () => {
        onClose();
        // Redirect to standard check-in modal or page logic
        // For now, we'll assume the user manually opens the check-in verified elsewhere, 
        // OR we could call the check-in API directly here if we had gate data.
        // Given "Gate Check-in" is usually its own physical process, 
        // we'll instruct them to use the main Check-In button.
        toast('Please proceed to Gate Check-In for this carrier.', { icon: '🚧' });
    };


    return (
        <Dialog open={!!appointmentId} onOpenChange={(open) => !open && onClose()}>
            <DialogPortal>
                <DialogOverlay />
                <DialogContent className="max-w-md">
                    <DialogHeader className="bg-red-50 dark:bg-red-900/10 border-b-red-100 dark:border-b-red-900/30">
                        <DialogTitle className="text-red-700 dark:text-red-400 flex items-center gap-2">
                            ⚠️ Resolve Delay Issue
                        </DialogTitle>
                        <p className="text-sm text-red-600/80 dark:text-red-400/70 mt-1">
                            Appointment {appointmentId.slice(-6).toUpperCase()} is critically late.
                        </p>
                    </DialogHeader>

                    <div className="p-6">
                        {action === 'decision' && (
                            <div className="grid gap-3">
                                <button
                                    onClick={() => handleCheckInRedirect()}
                                    className="p-4 rounded-lg border-2 border-emerald-100 hover:border-emerald-500 hover:bg-emerald-50 dark:border-emerald-900 dark:hover:border-emerald-500/50 dark:hover:bg-emerald-900/20 transition-all text-left group"
                                >
                                    <div className="font-bold text-emerald-700 dark:text-emerald-400 group-hover:underline">Arrival / Check-In</div>
                                    <div className="text-sm text-slate-500">Carrier has arrived. Proceed to gate check-in context.</div>
                                </button>

                                <button
                                    onClick={() => setAction('reason')}
                                    className="p-4 rounded-lg border-2 border-slate-100 hover:border-blue-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:border-blue-500/50 dark:hover:bg-slate-700/50 transition-all text-left"
                                >
                                    <div className="font-bold text-slate-700 dark:text-slate-200">Provide Reason</div>
                                    <div className="text-sm text-slate-500">Carrier is delayed but no new time yet.</div>
                                </button>

                                <button
                                    onClick={() => setAction('eta')}
                                    className="p-4 rounded-lg border-2 border-slate-100 hover:border-cyan-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:border-cyan-500/50 dark:hover:bg-slate-700/50 transition-all text-left"
                                >
                                    <div className="font-bold text-slate-700 dark:text-slate-200">Update ETA</div>
                                    <div className="text-sm text-slate-500">Set a new expected arrival time.</div>
                                </button>
                            </div>
                        )}

                        {action === 'reason' && (
                            <div className="space-y-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Reason Code</label>
                                <select
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full h-10 rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3"
                                >
                                    <option value="">Select a reason...</option>
                                    {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                                <div className="flex gap-2 justify-end mt-4">
                                    <button onClick={() => setAction('decision')} className="text-sm text-slate-500 hover:text-slate-800 px-3 py-2">Back</button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!reason || loading}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
                                    >
                                        {loading ? 'Saving...' : 'Submit Reason'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {action === 'eta' && (
                            <div className="space-y-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">New Expected Arrival</label>
                                <input
                                    type="datetime-local"
                                    value={newEta}
                                    onChange={(e) => setNewEta(e.target.value)}
                                    className="w-full h-10 rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3"
                                />
                                <p className="text-xs text-slate-500">Setting a new ETA resets the 15-minute alert timer.</p>

                                <div className="flex gap-2 justify-end mt-4">
                                    <button onClick={() => setAction('decision')} className="text-sm text-slate-500 hover:text-slate-800 px-3 py-2">Back</button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!newEta || loading}
                                        className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
                                    >
                                        {loading ? 'Saving...' : 'Update ETA'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
}
